import { addDays, toIsoDate } from './tasks.js';

// Grid's quick-add line: "Fix jump buffer p1 @jono #combat tomorrow" becomes a
// task titled "Fix jump buffer", priority 1, assigned to Jono, labelled combat,
// due tomorrow. Tokens can go anywhere; whatever isn't a token is the title.

export interface QuickAdd {
  title: string;
  /** 1..4 from p1..p4, or null when not given. */
  priority: 1 | 2 | 3 | 4 | null;
  /** What followed @, for matching against members. */
  assignee: string | null;
  /** What followed each #, without duplicates. */
  labels: string[];
  /** yyyy-mm-dd, or null. */
  due: string | null;
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

function weekday(word: string): number {
  if (word.length < 3) return -1;
  return WEEKDAYS.findIndex((d) => d.startsWith(word));
}

/** "nov", "november" and "sept" are months; "marching" isn't. */
function month(word: string): number {
  if (word.length < 3) return -1;
  if (word === 'sept') return 8;
  return MONTHS.findIndex((m) => m.startsWith(word));
}

/** A calendar date, or null when it doesn't exist (31 Feb). */
function makeDate(year: number, monthIndex: number, day: number): string | null {
  const d = new Date(year, monthIndex, day);
  return d.getMonth() === monthIndex && d.getDate() === day ? toIsoDate(d) : null;
}

/** The next time this day and month comes round, today included (29 Feb may be years away). */
function nextDayMonth(today: string, monthIndex: number, day: number): string | null {
  const year = Number(today.slice(0, 4));
  for (let y = year; y <= year + 4; y++) {
    const date = makeDate(y, monthIndex, day);
    if (date && date >= today) return date;
  }
  return null;
}

/**
 * Tries to read a date from `words` starting at `i`. Returns the date and how
 * many words it used, or null.
 */
function readDate(words: string[], i: number, today: string): { due: string; used: number } | null {
  const w = words[i]!.toLowerCase();
  const next = words[i + 1]?.toLowerCase();
  const after = words[i + 2]?.toLowerCase();

  if (w === 'today' || w === 'tod') return { due: today, used: 1 };
  if (w === 'tomorrow' || w === 'tmr' || w === 'tmrw') return { due: addDays(today, 1), used: 1 };

  if (/^\d{4}-\d{2}-\d{2}$/.test(w)) {
    const [y, m, d] = w.split('-').map(Number) as [number, number, number];
    const due = makeDate(y, m - 1, d);
    return due ? { due, used: 1 } : null;
  }

  if (w === 'next' && next === 'week') {
    const dow = new Date(`${today}T12:00:00`).getDay();
    return { due: addDays(today, (8 - dow) % 7 || 7), used: 2 };
  }

  if (w === 'in' && next && /^\d{1,3}$/.test(next) && after && /^(days?|weeks?)$/.test(after)) {
    const n = Number(next) * (after.startsWith('week') ? 7 : 1);
    return { due: addDays(today, n), used: 3 };
  }

  const wd = weekday(w);
  if (wd >= 0) {
    // The coming one; today counts.
    const dow = new Date(`${today}T12:00:00`).getDay();
    return { due: addDays(today, (wd - dow + 7) % 7), used: 1 };
  }

  // "5 nov" and "nov 5"
  if (/^\d{1,2}$/.test(w) && next && month(next) >= 0) {
    const due = nextDayMonth(today, month(next), Number(w));
    return due ? { due, used: 2 } : null;
  }
  if (month(w) >= 0 && next && /^\d{1,2}$/.test(next)) {
    const due = nextDayMonth(today, month(w), Number(next));
    return due ? { due, used: 2 } : null;
  }
  return null;
}

export function parseQuickAdd(input: string, now: Date = new Date()): QuickAdd {
  const today = toIsoDate(now);
  const words = input.trim().split(/\s+/).filter(Boolean);
  const title: string[] = [];
  const labels: string[] = [];
  let priority: QuickAdd['priority'] = null;
  let assignee: string | null = null;
  let due: string | null = null;

  for (let i = 0; i < words.length; i++) {
    const word = words[i]!;
    const p = /^p([1-4])$/i.exec(word);
    if (p) {
      priority = Number(p[1]) as 1 | 2 | 3 | 4;
      continue;
    }
    const at = /^@([\p{L}\p{N}._-]+)$/u.exec(word);
    if (at) {
      assignee = at[1]!;
      continue;
    }
    const hash = /^#([\p{L}\p{N}_-]+)$/u.exec(word);
    if (hash) {
      const name = hash[1]!;
      if (!labels.some((l) => l.toLowerCase() === name.toLowerCase())) labels.push(name);
      continue;
    }
    const date = readDate(words, i, today);
    if (date) {
      due = date.due;
      i += date.used - 1;
      continue;
    }
    title.push(word);
  }

  return { title: title.join(' '), priority, assignee, labels, due };
}
